import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardCheck, Plus, ChevronRight, CheckCircle2, AlertTriangle,
  Clock, Search, X, Filter, RefreshCw, ChevronDown, Lock, Trash2, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';

// ─── Constantes ───────────────────────────────────────────────────────────────

const ANCIENNETE_OPTS = [
  { id: null, label: 'Tous' },
  { id: 1,    label: '> 1 mois' },
  { id: 3,    label: '> 3 mois' },
  { id: 6,    label: '> 6 mois' },
  { id: 12,   label: '> 12 mois' },
];

function AncienneteSelect({ label, value, onChange }) {
  return (
    <div>
      <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {ANCIENNETE_OPTS.map(opt => (
          <button key={String(opt.id)} type="button" onClick={() => onChange(opt.id)}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
            style={{
              background: value === opt.id ? 'var(--primary-solid)' : 'white',
              color:      value === opt.id ? 'white' : 'var(--text-secondary)',
              borderColor: value === opt.id ? 'var(--primary-solid)' : 'var(--border)',
            }}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Panel création d'inventaire ──────────────────────────────────────────────

export function NouvelInventairePanel({ open, onClose, stockId, stockNom, zones, onCreated }) {
  const [titre, setTitre]               = useState('');
  const [datePlanifiee, setDatePlanifiee] = useState('');
  const [saving, setSaving]             = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Options chargées depuis l'API
  const [familles, setFamilles]         = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques]           = useState([]);
  const [loadingOpts, setLoadingOpts]   = useState(false);

  // Filtres sélectionnés
  const [selectedZones, setSelectedZones]             = useState([]);
  const [selectedFamilles, setSelectedFamilles]       = useState([]);
  const [selectedSousFamilles, setSelectedSousFamilles] = useState([]);
  const [selectedMarques, setSelectedMarques]         = useState([]);
  const [exclureZero, setExclureZero]                 = useState(true);
  const [derniereEntree, setDerniereEntree]           = useState(null);
  const [dernierInventaire, setDernierInventaire]     = useState(null);

  // Charger les options quand le panel s'ouvre
  useEffect(() => {
    if (!open || !stockId) return;
    setLoadingOpts(true);
    api.getInventaireOptions(stockId)
      .then(r => {
        setFamilles(r.data.familles || []);
        setSousFamilles(r.data.sous_familles || []);
        setMarques(r.data.marques || []);
      })
      .catch(() => {})
      .finally(() => setLoadingOpts(false));
    // Reset filtres
    setSelectedZones([]); setSelectedFamilles([]); setSelectedSousFamilles([]);
    setSelectedMarques([]); setExclureZero(true); setDerniereEntree(null);
    setDernierInventaire(null); setPreviewCount(null); setTitre(''); setDatePlanifiee('');
  }, [open, stockId]);

  // Sous-familles filtrées selon familles sélectionnées
  const sousFamillesFiltrees = selectedFamilles.length
    ? sousFamilles.filter(sf => selectedFamilles.includes(sf.famille_id))
    : sousFamilles;

  // Quand on désélectionne une famille, retirer ses sous-familles
  const toggleFamille = (id) => {
    setSelectedFamilles(s => {
      const next = s.includes(id) ? s.filter(x => x !== id) : [...s, id];
      if (!next.includes(id)) {
        const sfIds = sousFamilles.filter(sf => sf.famille_id === id).map(sf => sf.sous_famille_id);
        setSelectedSousFamilles(prev => prev.filter(x => !sfIds.includes(x)));
      }
      return next;
    });
  };

  const buildFiltres = () => ({
    zones:                  selectedZones.length        ? selectedZones        : undefined,
    familles:               selectedFamilles.length     ? selectedFamilles     : undefined,
    sous_familles:          selectedSousFamilles.length ? selectedSousFamilles : undefined,
    marques:                selectedMarques.length      ? selectedMarques      : undefined,
    exclure_zero:           exclureZero,
    derniere_entree_avant:  derniereEntree,
    dernier_inventaire_avant: dernierInventaire,
  });

  const refreshPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.previewInventaire({ stock_id: stockId, filtres: buildFiltres() });
      setPreviewCount(r.data.count);
    } catch { setPreviewCount(null); }
    finally { setLoadingPreview(false); }
  };

  const handleSubmit = async () => {
    if (!titre.trim()) { toast.error('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      const r = await api.createInventaire({
        titre: titre.trim(),
        stock_id: stockId,
        date_planifiee: datePlanifiee || undefined,
        filtres: buildFiltres(),
      });
      toast.success(`Inventaire créé — ${r.data.nb_total} article${r.data.nb_total !== 1 ? 's' : ''}`);
      onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally { setSaving(false); }
  };

  // Composant mini pour les chips de filtres
  const ChipGroup = ({ label, items, idKey, selectedIds, onToggle }) => {
    if (!items.length) return null;
    return (
      <div>
        <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label} <span style={{ color: 'var(--text-placeholder)', fontWeight: 400 }}>(tout si vide)</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {items.map(item => {
            const sel = selectedIds.includes(item[idKey]);
            return (
              <button key={item[idKey]} type="button"
                onClick={() => onToggle(item[idKey])}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all"
                style={{
                  background:  sel ? 'var(--primary-solid)' : 'white',
                  color:       sel ? 'white' : 'var(--text-secondary)',
                  borderColor: sel ? 'var(--primary-solid)' : 'var(--border)',
                }}>
                {item.nom}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={o => { if (!o) onClose(); }}
      title="Nouvel inventaire"
      subtitle={`Dépôt : ${stockNom}`}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          {previewCount !== null
            ? <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                {previewCount} article{previewCount !== 1 ? 's' : ''} concerné{previewCount !== 1 ? 's' : ''}
              </span>
            : <div />}
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium"
              style={{ color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}>
              Annuler
            </button>
            <button onClick={handleSubmit} disabled={saving || !titre.trim() || previewCount === 0}
              data-testid="create-inventaire-btn"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Création...' : 'Créer l\'inventaire'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Titre + date */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Titre *
            </label>
            <input value={titre} onChange={e => setTitre(e.target.value)}
              placeholder="Ex : Inventaire annuel 2026"
              data-testid="inventaire-titre-input"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Date planifiée
            </label>
            <input type="date" value={datePlanifiee} onChange={e => setDatePlanifiee(e.target.value)}
              data-testid="inventaire-date-input"
              className="w-full px-3.5 py-2.5 rounded-xl border text-sm outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          </div>
        </div>

        {/* Filtres */}
        <div className="space-y-4 p-4 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid var(--border-light)' }}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
              Filtres articles
            </p>
            <button type="button" onClick={refreshPreview} disabled={loadingPreview}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border"
              style={{ color: 'var(--text-secondary)', borderColor: 'var(--border)' }}>
              <Filter size={11} />{loadingPreview ? '...' : 'Prévisualiser'}
            </button>
          </div>

          {/* Exclure quantité zéro */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={exclureZero} onChange={e => setExclureZero(e.target.checked)}
              className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-solid)' }} />
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Exclure les articles avec quantité = 0
            </span>
          </label>

          {/* Dernière entrée */}
          <AncienneteSelect
            label="Dernière entrée/sortie remontant à plus de..."
            value={derniereEntree}
            onChange={setDerniereEntree}
          />

          {/* Dernier inventaire */}
          <AncienneteSelect
            label="Dernier inventaire remontant à plus de..."
            value={dernierInventaire}
            onChange={setDernierInventaire}
          />

          {loadingOpts && (
            <div className="flex items-center gap-2 text-xs py-1" style={{ color: 'var(--text-muted)' }}>
              <div className="animate-spin w-3 h-3 border border-t-transparent rounded-full"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
              Chargement des options…
            </div>
          )}

          {/* Zones */}
          {zones.length > 0 && (
            <ChipGroup
              label="Zones"
              items={zones.map(z => ({ zone_id: z.zone_id, nom: z.nom }))}
              idKey="zone_id"
              selectedIds={selectedZones}
              onToggle={id => setSelectedZones(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])}
            />
          )}

          {/* Familles */}
          <ChipGroup
            label="Familles"
            items={familles}
            idKey="famille_id"
            selectedIds={selectedFamilles}
            onToggle={toggleFamille}
          />

          {/* Sous-familles (filtrées selon famille sélectionnée) */}
          <ChipGroup
            label={selectedFamilles.length ? `Sous-familles (de la sélection)` : `Sous-familles`}
            items={sousFamillesFiltrees}
            idKey="sous_famille_id"
            selectedIds={selectedSousFamilles}
            onToggle={id => setSelectedSousFamilles(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])}
          />

          {/* Marques */}
          <ChipGroup
            label="Marques"
            items={marques}
            idKey="marque_id"
            selectedIds={selectedMarques}
            onToggle={id => setSelectedMarques(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])}
          />
        </div>
      </div>
    </SidePanel>
  );
}

// ─── Tri naturel ──────────────────────────────────────────────────────────────
function naturalSort(a, b) {
  return (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });
}

// ─── Ligne d'article dans la session (mobile-first) ───────────────────────────

function LigneArticle({ ligne, onUpdate, disabled }) {
  const theorique = parseFloat(ligne.quantite_theorique) || 0;
  const isSaisi = ligne.statut === 'saisi';
  const [val, setVal] = useState(
    ligne.quantite_comptee !== null && ligne.quantite_comptee !== undefined
      ? parseFloat(ligne.quantite_comptee)
      : theorique
  );
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(isSaisi);

  const ecart = val - theorique;

  useEffect(() => {
    const v = ligne.quantite_comptee !== null && ligne.quantite_comptee !== undefined
      ? parseFloat(ligne.quantite_comptee) : theorique;
    setVal(v);
  }, [ligne.quantite_comptee, theorique]);

  const handleSave = async () => {
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    try {
      await onUpdate(ligne.article_id, { quantite_comptee: val, commentaire: ligne.commentaire || '' });
      setLocked(true);
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const adjust = (delta) => setVal(v => Math.max(0, (isNaN(v) ? theorique : v) + delta));

  const statusColor = !isSaisi ? '#D1D5DB' : ecart !== 0 ? '#F59E0B' : '#16A34A';

  // Active : 5 colonnes  |  Clôturée : 4 colonnes (sans boutons)
  const GRID = disabled ? '1fr 44px 52px 44px' : '1fr 44px 52px 88px 44px';

  return (
    <div
      data-testid={`inventaire-ligne-${ligne.article_id}`}
      className="grid items-center gap-x-2 px-3 py-2.5 border-b last:border-0"
      style={{
        gridTemplateColumns: GRID,
        borderColor: 'var(--border-light)',
        background: isSaisi && ecart !== 0 ? '#FFFBEB' : isSaisi ? '#F0FDF4' : 'white',
      }}>

      {/* Col 1 : ref + désignation */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
          <span className="text-sm font-extrabold tracking-wide flex-shrink-0"
            style={{ color: 'var(--text)',  }}>
            {ligne.article_ref}
          </span>
          <span className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {ligne.article_nom}
          </span>
        </div>
        {ligne.commentaire && (
          <p className="text-xs italic pl-3.5 mt-0.5" style={{ color: '#92400E' }}>{ligne.commentaire}</p>
        )}
      </div>

      {/* Col 2 : Théorique — centré */}
      <div className="flex items-center justify-center">
        <span className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{theorique}</span>
      </div>

      {/* Col 3 : Compté — input (édition) | valeur centrée (verrouillé/clôturé) */}
      <div className="flex items-center justify-center">
        {!disabled && !locked ? (
          <input
            type="number" min="0" step="1" value={val}
            onChange={e => setVal(e.target.value === '' ? '' : parseFloat(e.target.value))}
            onFocus={e => e.target.select()}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            data-testid={`inventaire-qte-input-${ligne.article_id}`}
            className="w-full h-7 px-1 rounded-lg border text-sm font-bold text-center outline-none"
            style={{ borderColor: isSaisi && ecart !== 0 ? '#F59E0B' : 'var(--border)', color: 'var(--text)' }}
          />
        ) : (
          <span className="text-sm font-bold"
            style={{ color: isSaisi ? (ecart !== 0 ? '#D97706' : '#16A34A') : 'var(--text-muted)' }}>
            {isSaisi ? val : '—'}
          </span>
        )}
      </div>

      {/* Col 4 : boutons (actif seulement) */}
      {!disabled && (
        locked ? (
          /* Cadenas centré */
          <div className="flex items-center justify-center">
            <button
              onClick={() => setLocked(false)}
              data-testid={`inventaire-unlock-${ligne.article_id}`}
              title="Modifier la saisie"
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
              style={{ background: '#16A34A' }}>
              <Lock size={13} color="white" />
            </button>
          </div>
        ) : (
          /* [-][+][✓] alignés en flex */
          <div className="flex items-center justify-between">
            <button onPointerDown={() => adjust(-1)}
              data-testid={`inventaire-minus-${ligne.article_id}`}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold"
              style={{ background: '#F3F4F6', color: '#374151' }}>−</button>
            <button onPointerDown={() => adjust(1)}
              data-testid={`inventaire-plus-${ligne.article_id}`}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-base font-bold"
              style={{ background: '#F3F4F6', color: '#374151' }}>+</button>
            <button onClick={handleSave} disabled={saving}
              data-testid={`inventaire-save-${ligne.article_id}`}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
              style={{ background: saving ? '#D1D5DB' : '#16A34A' }}>
              {saving
                ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                : <CheckCircle2 size={14} color="white" />}
            </button>
          </div>
        )
      )}

      {/* Dernière col : Écart */}
      <div className="flex items-center justify-center">
        {isSaisi && ecart !== 0 ? (
          <span className="text-xs font-bold"
            style={{ color: ecart > 0 ? '#16A34A' : '#DC2626' }}>
            {ecart > 0 ? '+' : ''}{ecart}
          </span>
        ) : null}
      </div>

    </div>
  );
}

// ─── Vue détail d'une session (mobile-first) ──────────────────────────────────

export function InventaireDetail({ sessionId, onBack, onUpdated }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEcart, setFilterEcart] = useState('');
  const [cloturing, setCloturing] = useState(false);
  const [showCloture, setShowCloture] = useState(false);
  const [regulariser, setRegulariser] = useState(true);

  // ── Ajout article ──────────────────────────────────────────────────────────
  const [showAjout, setShowAjout]               = useState(false);
  const [ajoutSearch, setAjoutSearch]           = useState('');
  const [ajoutResults, setAjoutResults]         = useState([]);
  const [ajoutSearching, setAjoutSearching]     = useState(false);
  const [ajoutSelected, setAjoutSelected]       = useState(null);
  const [ajoutZones, setAjoutZones]             = useState([]);
  const [ajoutZoneId, setAjoutZoneId]           = useState('');
  const [ajoutSaving, setAjoutSaving]           = useState(false);
  const ajoutSearchRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.getInventaire(sessionId);
      setSession(r.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { load(); }, [load]);

  // Charger les zones quand on ouvre la modale d'ajout
  useEffect(() => {
    if (showAjout && session?.stock_id) {
      api.getZones(session.stock_id).then(r => setAjoutZones(r?.data || [])).catch(() => {});
      setTimeout(() => ajoutSearchRef.current?.focus(), 100);
    }
    if (!showAjout) { setAjoutSearch(''); setAjoutResults([]); setAjoutSelected(null); setAjoutZoneId(''); }
  }, [showAjout, session?.stock_id]);

  // Recherche article (debounce 300ms)
  useEffect(() => {
    if (!ajoutSearch.trim() || ajoutSearch.length < 2) { setAjoutResults([]); return; }
    setAjoutSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.getArticles({ q: ajoutSearch, limit: 20, gestion_stock: true });
        setAjoutResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch { setAjoutResults([]); }
      finally { setAjoutSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [ajoutSearch]);

  const handleAjoutSubmit = async () => {
    if (!ajoutSelected) return;
    setAjoutSaving(true);
    try {
      const r = await api.ajouterLigneInventaire(sessionId, {
        article_id: ajoutSelected.article_id,
        zone_id: ajoutZoneId || null,
      });
      setSession(prev => ({
        ...prev,
        ...r.data.session,
        lignes: [...(prev.lignes || []), r.data.ligne],
      }));
      if (onUpdated) onUpdated(r.data.session);
      toast.success(`${ajoutSelected.reference} ajouté à l'inventaire`);
      setShowAjout(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally { setAjoutSaving(false); }
  };

  const handleUpdate = async (articleId, data) => {
    const r = await api.updateInventaireLigne(sessionId, articleId, data);
    setSession(prev => ({
      ...prev,
      ...r.data.session,
      lignes: prev.lignes.map(l => l.article_id === articleId ? r.data.ligne : l),
    }));
    if (onUpdated) onUpdated(r.data.session);
  };

  const handleCloture = async () => {
    setCloturing(true);
    try {
      const r = await api.cloturerInventaire(sessionId, { regulariser });
      toast.success(regulariser ? 'Inventaire clôturé — stock régularisé' : 'Inventaire clôturé');
      setSession(prev => ({ ...prev, ...r.data }));
      setShowCloture(false);
      if (onUpdated) onUpdated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la clôture');
    } finally { setCloturing(false); }
  };

  const handleExportPdf = async () => {
    try {
      const r = await api.exportInventairePdf(sessionId);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventaire_${session.titre || sessionId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!session) return null;

  const nbNonSaisies = (session.nb_total || 0) - (session.nb_saisi || 0);

  const isTerminee = session.statut === 'cloturee';
  const pct = session.nb_total > 0 ? Math.round((session.nb_saisi / session.nb_total) * 100) : 0;

  // Filtrage
  const lignesFiltrees = (session.lignes || []).filter(l => {
    const s = search.toLowerCase();
    const matchSearch = !s
      || (l.article_nom || '').toLowerCase().includes(s)
      || (l.article_ref || '').toLowerCase().includes(s)
      || (l.zone_nom    || '').toLowerCase().includes(s);
    const matchFilter = filterEcart === 'ecart'      ? (l.statut === 'saisi' && l.ecart !== 0)
                       : filterEcart === 'en_attente' ? l.statut === 'en_attente'
                       : true;
    return matchSearch && matchFilter;
  });

  // Groupement par zone, tri naturel
  const zones = [...new Set(lignesFiltrees.map(l => l.zone_nom || ''))].sort(naturalSort);
  const grouped = zones.map(z => ({
    zone: z,
    lignes: lignesFiltrees
      .filter(l => (l.zone_nom || '') === z)
      .sort((a, b) => naturalSort(a.article_ref || '', b.article_ref || '')),
  }));

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: '100%' }}>
      {/* Header compact */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-shrink-0">
        <button onClick={onBack}
          className="flex items-center gap-1 text-xs font-medium hover:underline flex-shrink-0"
          style={{ color: 'var(--text-muted)' }}>
          ← Retour
        </button>
        <div className="flex-1 min-w-0 text-center">
          <p className="text-sm font-bold truncate" style={{  color: 'var(--text)' }}>
            {session.titre}
          </p>
        </div>
        {!isTerminee && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => setShowAjout(true)}
              data-testid="ajout-article-inventaire-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold border"
              style={{ color: 'var(--primary-solid)', borderColor: 'var(--primary-solid)', background: 'var(--primary-50, #EFF6FF)' }}>
              <Plus size={11} /> Article
            </button>
            <button onClick={handleExportPdf}
              data-testid="export-pdf-inventaire-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold border"
              style={{ color: 'var(--text-secondary)', borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <FileText size={11} /> PDF
            </button>
            <button onClick={() => setShowCloture(true)}
              data-testid="cloture-inventaire-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold border flex-shrink-0"
              style={{ color: 'var(--primary-solid)', borderColor: 'var(--primary-solid)', background: 'var(--primary-50, #EFF6FF)' }}>
              <Lock size={11} /> Clôturer
            </button>
          </div>
        )}
      </div>

      {/* Statut + progression */}
      <div className="flex items-center gap-3 mb-2 flex-shrink-0">
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
          <div style={{ width: `${pct}%`, background: session.nb_ecart > 0 ? '#F59E0B' : 'var(--primary-solid)', transition: 'width 0.3s', height: '100%' }} />
        </div>
        <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--text-muted)', minWidth: 60, textAlign: 'right' }}>
          {session.nb_saisi}/{session.nb_total} ({pct}%)
        </span>
      </div>

      {/* Barre recherche + filtre */}
      <div className="flex gap-2 mb-2 flex-shrink-0">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Réf., nom, zone..."
            data-testid="inventaire-detail-search"
            className="w-full pl-7 pr-7 py-2 rounded-xl border text-sm outline-none"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X size={12} style={{ color: 'var(--text-muted)' }} /></button>}
        </div>
        <select value={filterEcart} onChange={e => setFilterEcart(e.target.value)}
          className="px-2 py-2 rounded-xl border text-xs font-semibold outline-none"
          style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
          <option value="">Tous</option>
          <option value="en_attente">Attente</option>
          <option value="ecart">Écarts</option>
        </select>
      </div>

      {/* Liste groupée avec entêtes de colonnes fixes */}
      <div className="flex-1 rounded-xl border overflow-hidden flex flex-col"
        style={{ borderColor: 'var(--border-light)', minHeight: 0 }}>
        {/* Entête colonnes sticky */}
        <div className="grid flex-shrink-0 px-3 py-1.5 text-xs font-bold border-b gap-x-2"
          style={{
            gridTemplateColumns: isTerminee ? '1fr 44px 52px 44px' : '1fr 44px 52px 88px 44px',
            background: '#F8FAFC',
            borderColor: 'var(--border-light)',
            color: 'var(--text-muted)',
          }}>
          <span className="whitespace-nowrap">Référence / Désignation</span>
          <div className="flex items-center justify-center whitespace-nowrap">Théo.</div>
          <div className="flex items-center justify-center whitespace-nowrap">Compté</div>
          {!isTerminee && <div />}
          <div className="flex items-center justify-center whitespace-nowrap">Écart</div>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 overflow-y-auto">
          {lignesFiltrees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardCheck size={36} className="mb-2" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {search ? 'Aucun résultat' : 'Aucune ligne'}
              </p>
            </div>
          ) : grouped.map(({ zone, lignes: ligs }) => (
            <div key={zone || '__sans_zone__'}>
              {/* Entête de zone */}
              <div className="px-3 py-1.5 text-xs font-bold sticky top-0 z-10"
                style={{ background: '#EFF6FF', color: 'var(--primary-solid)', borderBottom: '1px solid var(--border-light)' }}>
                {zone || 'Sans zone'} <span className="font-normal" style={{ color: 'var(--text-muted)' }}>— {ligs.length} article{ligs.length !== 1 ? 's' : ''}</span>
              </div>
              {ligs.map(ligne => (
                <LigneArticle
                  key={ligne.article_id}
                  ligne={ligne}
                  onUpdate={handleUpdate}
                  disabled={isTerminee}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-center mt-1 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
        {lignesFiltrees.length} article{lignesFiltrees.length !== 1 ? 's' : ''}
        {session.nb_ecart > 0 && <span style={{ color: '#D97706', marginLeft: 8 }}>· {session.nb_ecart} écart{session.nb_ecart > 1 ? 's' : ''}</span>}
      </p>

      {/* Modale ajout article */}
      {showAjout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowAjout(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Ajouter un article</p>
              <button onClick={() => setShowAjout(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X size={15} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Recherche article */}
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Article</p>
                {ajoutSelected ? (
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-xl border"
                    style={{ borderColor: 'var(--primary-solid)', background: 'var(--primary-50, #EFF6FF)' }}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{ajoutSelected.reference}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{ajoutSelected.designation}</p>
                    </div>
                    <button onClick={() => { setAjoutSelected(null); setAjoutSearch(''); }}
                      className="p-1 rounded-lg hover:bg-white">
                      <X size={13} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                    <input
                      ref={ajoutSearchRef}
                      value={ajoutSearch}
                      onChange={e => setAjoutSearch(e.target.value)}
                      placeholder="Référence ou désignation…"
                      className="w-full pl-8 pr-3 py-2 rounded-xl border text-sm outline-none"
                      style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                    />
                    {ajoutSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                    )}
                    {ajoutResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-lg z-10 max-h-48 overflow-y-auto"
                        style={{ borderColor: 'var(--border)' }}>
                        {ajoutResults.map(art => (
                          <button key={art.article_id}
                            onClick={() => {
                              setAjoutSelected(art);
                              setAjoutSearch('');
                              setAjoutResults([]);
                              // Récupérer la zone existante de cet article dans ce stock
                              if (session?.stock_id) {
                                api.getStockArticles(session.stock_id, { article_id: art.article_id })
                                  .then(r => {
                                    const entries = Array.isArray(r.data) ? r.data : [];
                                    const entry = entries.find(e => e.article_id === art.article_id);
                                    if (entry?.zone_id) setAjoutZoneId(entry.zone_id);
                                  })
                                  .catch(() => {});
                              }
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b last:border-0 transition-colors"
                            style={{ borderColor: 'var(--border-light)' }}>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{art.reference}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{art.designation}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Zone de rangement */}
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Zone de rangement <span style={{ color: '#DC2626' }}>*</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — sera attribuée à l'article</span>
                </p>
                <select value={ajoutZoneId} onChange={e => setAjoutZoneId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                  style={{ borderColor: !ajoutZoneId ? '#FCA5A5' : 'var(--border)', color: 'var(--text)' }}>
                  <option value="">— Sélectionner une zone —</option>
                  {ajoutZones.map(z => (
                    <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={() => setShowAjout(false)}
                className="flex-1 px-4 py-2 rounded-xl border text-sm font-medium"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                Annuler
              </button>
              <button onClick={handleAjoutSubmit}
                disabled={!ajoutSelected || !ajoutZoneId || ajoutSaving}
                data-testid="ajout-article-inventaire-submit"
                className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--primary-solid)' }}>
                {ajoutSaving ? 'Ajout…' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog clôture */}
      {showCloture && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold mb-1" style={{  color: 'var(--text)' }}>
              Clôturer l'inventaire
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              {session.nb_saisi}/{session.nb_total} saisis — {session.nb_ecart} écart{session.nb_ecart !== 1 ? 's' : ''}.
            </p>
            {nbNonSaisies > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-xl mb-4"
                style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
                <AlertTriangle size={14} style={{ color: '#B45309', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs font-medium" style={{ color: '#92400E' }}>
                  {nbNonSaisies} ligne{nbNonSaisies > 1 ? 's' : ''} non saisie{nbNonSaisies > 1 ? 's' : ''} — la clôture sera bloquée par le serveur.
                  Complétez le comptage avant de clôturer.
                </p>
              </div>
            )}
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer mb-4"
              style={{ borderColor: regulariser ? 'var(--primary-solid)' : 'var(--border-light)', background: regulariser ? 'var(--primary-50, #EFF6FF)' : '#F9FAFB' }}>
              <input type="checkbox" checked={regulariser} onChange={e => setRegulariser(e.target.checked)}
                className="mt-0.5" style={{ accentColor: 'var(--primary-solid)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Régulariser le stock</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  Ajuste les quantités pour correspondre aux comptages ({session.nb_ecart} mouvement{session.nb_ecart !== 1 ? 's' : ''})
                </p>
              </div>
            </label>
            <div className="flex gap-3">
              <button onClick={() => setShowCloture(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium border"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
                Annuler
              </button>
              <button onClick={handleCloture} disabled={cloturing}
                data-testid="confirm-cloture-btn"
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                style={{ background: 'var(--primary-gradient)' }}>
                {cloturing ? 'Clôture...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Inventaire principal ──────────────────────────────────────────────

export default function StocksInventaireTab({ stockId, stockNom, zones, stockArticles }) {
  const [sessions, setSessions]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showPanel, setShowPanel]     = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Familles distinctes des articles de ce stock
  const familles = [...new Set((stockArticles || []).map(a => a.famille).filter(Boolean))].sort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getInventaires(stockId);
      setSessions(r.data || []);
      setHasActiveSession((r.data || []).some(s => s.statut === 'en_cours'));
    } catch { toast.error('Erreur chargement des inventaires'); }
    finally { setLoading(false); }
  }, [stockId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (session) => {
    setSessions(prev => [session, ...prev]);
    setHasActiveSession(true);
    setShowPanel(false);
    setSelectedId(session.session_id);
  };

  const handleUpdated = (updated) => {
    setSessions(prev => prev.map(s => s.session_id === updated.session_id ? { ...s, ...updated } : s));
    if (updated.statut === 'cloturee') setHasActiveSession(sessions.some(s => s.session_id !== updated.session_id && s.statut === 'en_cours'));
  };

  const handleDelete = async (sessionId) => {
    try {
      await api.deleteInventaire(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (selectedId === sessionId) setSelectedId(null);
      setHasActiveSession(sessions.some(s => s.session_id !== sessionId && s.statut === 'en_cours'));
      toast.success('Session supprimée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  // Détail ouvert
  if (selectedId) {
    return (
      <InventaireDetail
        sessionId={selectedId}
        onBack={() => setSelectedId(null)}
        onUpdated={handleUpdated}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
            Sessions d'inventaire
          </p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { if (hasActiveSession) { toast.error('Un inventaire est déjà en cours pour ce stock'); return; } setShowPanel(true); }}
          data-testid="new-inventaire-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-all"
          style={{ background: hasActiveSession ? '#9CA3AF' : 'var(--primary-gradient)', cursor: hasActiveSession ? 'not-allowed' : 'pointer' }}>
          {hasActiveSession ? <Lock size={12} /> : <Plus size={12} />}
          {hasActiveSession ? 'Session en cours' : 'Nouvel inventaire'}
        </button>
      </div>

      {/* Avertissement inventaire actif */}
      {hasActiveSession && (
        <div className="flex items-start gap-2 p-3 rounded-xl text-sm"
          style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}>
          <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
          <p>Un inventaire est en cours. Les mouvements de stock sont déconseillés pendant cette période.</p>
        </div>
      )}

      {/* Liste des sessions */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-7 h-7 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border"
          style={{ borderColor: 'var(--border-light)', background: '#FAFAFA' }}>
          <ClipboardCheck size={36} className="mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Aucun inventaire</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Créez votre première session d'inventaire</p>
        </div>
      ) : sessions.map(s => {
        const pct = s.nb_total > 0 ? Math.round((s.nb_saisi / s.nb_total) * 100) : 0;
        const isActive = s.statut === 'en_cours';
        return (
          <div key={s.session_id}
            data-testid={`inventaire-session-${s.session_id}`}
            className="rounded-xl border p-4 cursor-pointer hover:shadow-sm transition-all"
            style={{ borderColor: isActive ? 'var(--primary-solid)' : 'var(--border-light)', background: 'white' }}
            onClick={() => setSelectedId(s.session_id)}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold truncate" style={{ color: 'var(--text)',  }}>
                    {s.titre}
                  </span>
                  {isActive
                    ? <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#FEF3C7', color: '#D97706' }}>En cours</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#F0FDF4', color: '#16A34A' }}>Clôturé</span>}
                  {s.regularise && <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#2563EB' }}>Régularisé</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {new Date(s.date_creation).toLocaleDateString('fr-FR')}
                  {s.nb_ecart > 0 && <span style={{ color: '#D97706', marginLeft: '8px' }}>· {s.nb_ecart} écart{s.nb_ecart > 1 ? 's' : ''}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isActive && (
                  <button
                    data-testid={`delete-session-${s.session_id}`}
                    onClick={e => {
                      e.stopPropagation();
                      if (window.confirm('Supprimer cette session d\'inventaire ? Cette action est irréversible.')) {
                        handleDelete(s.session_id);
                      }
                    }}
                    className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
                    style={{ color: '#DC2626' }}
                    title="Supprimer la session">
                    <Trash2 size={14} />
                  </button>
                )}
                <span className="text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{pct}%</span>
                <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
              <div style={{ width: `${pct}%`, background: s.nb_ecart > 0 ? '#F59E0B' : 'var(--primary-solid)', transition: 'width 0.3s' }} />
            </div>
            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
              {s.nb_saisi} / {s.nb_total} articles saisis
            </p>
          </div>
        );
      })}

      {showPanel && (
        <NouvelInventairePanel
          open={true}
          onClose={() => setShowPanel(false)}
          stockId={stockId}
          stockNom={stockNom}
          zones={zones}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
